
import { useEffect, useState } from 'react';
import { qrCodeService, shortLinkService, bioLinkService } from '../../../utils/storage';
import { filterByDateRange, groupByHour, groupByDay } from '../../../utils/analytics';

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  type: 'qr' | 'link' | 'bio';
}

interface AnalyticsData {
  id: string;
  scanned_at?: string;
  clicked_at?: string;
  viewed_at?: string;
  country: string;
  city: string;
  device_type: string;
  os: string;
  browser: string;
}

export default function AnalyticsModal({ isOpen, onClose, itemId, itemName, type }: AnalyticsModalProps) {
  const [data, setData] = useState<AnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('7d');

  useEffect(() => {
    if (isOpen) {
      loadAnalytics();
    }
  }, [isOpen, itemId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      let analyticsData: AnalyticsData[] = [];

      if (type === 'qr') {
        analyticsData = await qrCodeService.getAnalytics(itemId);
      } else if (type === 'link') {
        analyticsData = await shortLinkService.getAnalytics(itemId);
      } else if (type === 'bio') {
        analyticsData = await bioLinkService.getAnalytics(itemId);
      }

      setData(analyticsData);
    } catch (error) {
      console.error('분석 데이터 로드 실패:', error);
      // Add error state to show user-friendly message
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredData = filterByDateRange(data, dateRange);
  const hourlyData = groupByHour(filteredData);
  const dailyData = groupByDay(filteredData);

  // 통계 계산
  const totalCount = filteredData.length;
  
  // Add defensive check for empty data
  const deviceStats = filteredData.reduce((acc, item) => {
    const deviceType = item.device_type || 'Unknown';
    acc[deviceType] = (acc[deviceType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const countryStats = filteredData.reduce((acc, item) => {
    const country = item.country || 'Unknown';
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const browserStats = filteredData.reduce((acc, item) => {
    const browser = item.browser || 'Unknown';
    acc[browser] = (acc[browser] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const actionText = type === 'qr' ? '스캔' : type === 'link' ? '클릭' : '조회';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">분석 리포트</h2>
            <p className="text-sm text-gray-600 mt-1">{itemName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            aria-label="닫기"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
              <p className="text-gray-600 mt-4">로딩 중...</p>
            </div>
          ) : (
            <>
              {/* Date Range Filter */}
              <div className="flex gap-2 flex-wrap">
                {(['7d', '30d', '90d', 'all'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setDateRange(range)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      dateRange === range
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {range === '7d' ? '최근 7일' : range === '30d' ? '최근 30일' : range === '90d' ? '최근 90일' : '전체'}
                  </button>
                ))}
              </div>

              {/* Total Count */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-teal-100 text-sm">총 {actionText} 수</p>
                    <p className="text-4xl font-bold mt-2">{totalCount.toLocaleString()}</p>
                  </div>
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full">
                    <i className={`text-3xl ${
                      type === 'qr' ? 'ri-qr-scan-2-line' : type === 'link' ? 'ri-cursor-line' : 'ri-eye-line'
                    }`}></i>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Device Stats */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-device-line text-teal-500"></i>
                    디바이스
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(deviceStats).map(([device, count]) => (
                      <div key={device} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{device}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-teal-500 rounded-full transition-all duration-300"
                              style={{ width: `${totalCount > 0 ? (count / totalCount) * 100 : 0}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                            {count}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Browser Stats */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-global-line text-teal-500"></i>
                    브라우저
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(browserStats).map(([browser, count]) => (
                      <div key={browser} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{browser}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-teal-500 rounded-full transition-all duration-300"
                              style={{ width: `${totalCount > 0 ? (count / totalCount) * 100 : 0}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                            {count}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Country Stats */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-map-pin-line text-teal-500"></i>
                    국가
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(countryStats)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([country, count]) => (
                        <div key={country} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{country}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-teal-500 rounded-full transition-all duration-300"
                                style={{ width: `${totalCount > 0 ? (count / totalCount) * 100 : 0}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                              {count}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Daily Chart */}
              {dailyData.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-line text-teal-500"></i>
                    일별 {actionText}
                  </h3>
                  <div className="space-y-2">
                    {dailyData.slice(-14).map((item) => {
                      const maxCount = Math.max(...dailyData.map(d => d.count), 1);
                      return (
                        <div key={item.day} className="flex items-center gap-3">
                          <span className="text-xs text-gray-600 w-20">
                            {new Date(item.day).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                          </span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-teal-400 to-teal-500 rounded-lg flex items-center justify-end px-3 transition-all duration-300"
                              style={{ width: `${(item.count / maxCount) * 100}%` }}
                            >
                              <span className="text-xs font-semibold text-white">{item.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-time-line text-teal-500"></i>
                  최근 활동
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredData.length > 0 ? (
                    filteredData.slice(0, 20).map((item) => {
                      const timestamp = new Date(
                        item.scanned_at || item.clicked_at || item.viewed_at || Date.now()
                      );
                      return (
                        <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg">
                              <i className={`text-teal-500 ${
                                item.device_type === 'Mobile' ? 'ri-smartphone-line' :
                                item.device_type === 'Tablet' ? 'ri-tablet-line' :
                                'ri-computer-line'
                              }`}></i>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.city || 'Unknown'}, {item.country || 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.device_type || 'Unknown'} · {item.browser || 'Unknown'} · {item.os || 'Unknown'}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {timestamp.toLocaleString('ko-KR', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <i className="ri-inbox-line text-3xl mb-2"></i>
                      <p>활동 데이터가 없습니다</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
